import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, TrendingUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useMode } from "../context/ModeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REALM_TO_PATH = {
  marketing: "/dashboard/ai-video",
  designer:  "/dashboard/designer",
  cgi:       "/dashboard/cgi",
  movie:     "/dashboard/movie",
};

const SIGNAL_LABEL = {
  style:        "style",
  music_genre:  "music vibe",
  genre:        "genre",
  tone:         "tone",
  voice:        "voice",
  polish_mode:  "polish",
  aspect_ratio: "aspect",
  duration:     "duration",
  kling_model:  "model",
  length:       "length",
};

/**
 * "For you" chip rendered in the dashboard header. Shows a tiny personalized
 * line + a "trending in this realm" hint when the brain has data.
 *
 * • Reads the active mode from ModeContext and asks `/api/brain/for-you/{realm}`.
 * • Renders nothing when both `you` and `trend` are null (cold-start UX).
 * • Color-bound to the mode accent so it blends with the rest of the wash.
 * • Tooltip line links to the realm's home page when clicked.
 */
export default function ForYouChip() {
  const { token } = useAuth();
  let realm = "marketing";
  let accent = "#FF4F27";
  try {
    const m = useMode();
    realm = m.mode || "marketing";
    accent = m.config?.accent || accent;
  } catch (_e) { /* outside provider */ }

  const [data, setData] = useState({ you: null, trend: null });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!token || !realm) return;
      try {
        const r = await axios.get(`${API}/brain/for-you/${realm}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancel) {
          setData(r.data || { you: null, trend: null });
          setLoaded(true);
        }
      } catch (_e) {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, [token, realm]);

  if (!loaded) return null;
  const you = data?.you;
  const trend = data?.trend;
  if (!you && !trend) return null;

  const link = REALM_TO_PATH[realm] || "/dashboard";

  return (
    <AnimatePresence>
      <motion.a
        href={link}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="hidden sm:inline-flex items-center gap-2 rounded-full border bg-white/80 backdrop-blur-md px-3 py-1.5 shadow-sm hover:shadow-md transition-shadow"
        style={{ borderColor: `${accent}33` }}
        data-testid="for-you-chip"
        title="XEA is learning your style"
      >
        <Sparkles className="w-3.5 h-3.5" style={{ color: accent }} />
        {you && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700">
            <span className="text-ink-500">For you · </span>
            <span style={{ color: accent }}>{SIGNAL_LABEL[you.signal] || you.signal}</span>
            <span className="text-ink-950">: {you.value}</span>
          </span>
        )}
        {you && trend && <span className="text-ink-300">|</span>}
        {trend && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700">
            <TrendingUp className="w-3 h-3 text-ink-500" />
            <span className="text-ink-500">trending · </span>
            <span className="text-ink-950">{trend.value}</span>
          </span>
        )}
      </motion.a>
    </AnimatePresence>
  );
}
